import React, { useState, useCallback, useMemo, useRef } from 'react';
import cyrillicToTranslit from 'cyrillic-to-translit-js';
import Fuse from 'fuse.js';
import ru from 'convert-layout/ru';
import { useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import Lottie from 'react-lottie-player';
import ReactGA from 'Shared/analytics';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';

import Table from 'Components/Table';
import Input from 'Components/Input';
import Icon from 'Components/Icon';
import formatDate from 'Shared/util/formatDate';
import LogoFirst from '../../assets/RandomCat1.png';
import LogoSecond from '../../assets/RandomCat2.png';
import getRandomInt from 'Shared/util/getRandomInt';
import animationData from 'Shared/spiner_blue.json';
import useDocumentTitle from 'Shared/hooks/useDocumentTitle';
import moment from 'moment';

import { queryGetColleagues } from '../../Colleagues';
import { logger } from 'RootShared/utility/ErrorLogger';

const transformEmployeesData = (colleagues) => {
  return colleagues
    ? colleagues.map(({ user: { firstName, lastName, ...userData }, ...colleagueData }) => {
        return {
          ...colleagueData,
          user: {
            fullName: `${firstName} ${lastName}`,
            ...userData,
          },
        };
      })
    : [];
};

const TabContactsTable = () => {
  const [searchInputValue, setSearchInputValue] = useState('');

  useDocumentTitle('Colleagues - Contacts');

  const { loading, data, error } = useQuery(queryGetColleagues, {
    options: { cache: 'cache-only' },
  });

  const employees = transformEmployeesData((data && data.colleagues) || []);

  const searchOptions = useMemo(
    () => ({
      shouldSort: true,
      threshold: 0.2,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: ['user.fullName', 'telegram'],
    }),
    []
  );

  const searchFuse = useMemo(() => {
    if (employees) {
      return new Fuse(employees, searchOptions);
    }
  }, [employees, searchOptions]);

  const filterEmployees = useCallback((searchFuse, searchInputValue) => {
    const searchEnglishOccurrences = searchFuse.search(ru.toEn(searchInputValue));
    if (/[а-яА-ЯЁё]/.test(searchInputValue)) {
      const searchOccurrences = searchFuse
        .search(cyrillicToTranslit().transform(searchInputValue))
        .concat(searchEnglishOccurrences);
      return searchOccurrences.filter((elem, index) => searchOccurrences.indexOf(elem) === index);
    }
    return searchEnglishOccurrences;
  }, []);

  const handleSearchInputChange = useCallback(
    (event) => {
      ReactGA.event({
        category: 'Colleagues Contacts',
        action: 'Search of employee',
      });
      setSearchInputValue(event.target.value);
    },
    [setSearchInputValue]
  );

  const mapColleaguesDataToRows = useCallback((colleagues = []) => {
    const filteredColleagues = colleagues.map(({ user, avatar, phone, telegram, birthDate }) => {
      const defaultAvatar = getRandomInt(2) === 0 ? LogoFirst : LogoSecond;

      return {
        avatar: {
          title: avatar ? avatar : defaultAvatar,
          userUrl: `${user.id}`,
        },
        name: {
          title: user.fullName.length > 1 ? user.fullName : '-',
        },
        phone: {
          title: phone ? (
            <a href={`tel:${phone}`} className="colleagues-link">
              {`${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6, 9)} ${phone.slice(9, 11)} ${phone.slice(
                11
              )}`}
            </a>
          ) : (
            '-'
          ),
        },
        telegram: {
          title: telegram ? (
            <a href={`tg://resolve?domain=${telegram}`} className="colleagues-link">{`@${telegram}`}</a>
          ) : (
            '-'
          ),
        },
        email: {
          title: user.email ? (
            <a href={`mailto:${user.email}`} className="colleagues-link">
              {user.email}
            </a>
          ) : (
            '-'
          ),
        },
        birth_date: {
          title: birthDate ? `${formatDate(birthDate)}` : '-',
        },
      };
    });

    return filteredColleagues.length
      ? filteredColleagues
      : [
          {
            not_found: {
              title: 'No contacts with this name',
            },
            name: { title: 'No contacts with this name' },
          },
        ];
  }, []);

  const searchInputRef = useRef(null);

  const searchInput = useMemo(() => {
    return (
      <>
        <span className="filter-title">NAME</span>
        <Input
          icon={<Icon name="icon-search" />}
          className="input-group-filter"
          initialFocusState={true}
          ref={searchInputRef}
          fieldProps={{
            name: 'search',
            placeholder: 'Search ...',
            value: searchInputValue,
            onChange: handleSearchInputChange,
            onClick: (event) => event.stopPropagation(),
          }}
        />
      </>
    );
  }, [searchInputValue, handleSearchInputChange]);

  const columns = [
    {
      cellProps: {
        Header: searchInput,
        Cell: (props) => {
          return (
            <>
              <span className="rt-td__head">NAME</span>
              <div className={'rt-td__cell'}>
                <div className="colleague-name-container" data-testid="colleague-name">
                  {props.row._original.avatar && props.row._original.avatar.title && (
                    <img
                      className="colleague-avatar"
                      src={props.row._original.avatar && props.row._original.avatar.title}
                      alt="colleague-avatar"
                    />
                  )}
                  <span className="colleague-name">{props.row.name.title}</span>
                </div>
              </div>
            </>
          );
        },
        accessor: 'name',
        width: 262,
      },
    },
    {
      title: 'PHONE',
      cellProps: {
        accessor: 'phone',
        width: 187,
        sortable: false,
      },
    },
    {
      title: 'TELEGRAM',
      cellProps: {
        accessor: 'telegram',
        width: 170,
        sortable: false,
      },
    },
    {
      title: 'EMAIL',
      cellProps: {
        accessor: 'email',
        width: 294,
        sortable: false,
      },
    },
    {
      title: 'BIRTH DATE',
      cellProps: {
        accessor: 'birth_date',
        width: 207,
        sortMethod: (a, b) => {
          const aDate = moment(a.title.split('·')[0], 'MMMM D');
          const bDate = moment(b.title.split('·')[0], 'MMMM D');
          return moment(aDate, 'MMMM D') - moment(bDate, 'MMMM D');
        },
      },
    },
  ];

  const computeTableProps = useCallback(
    (colleagues = []) => {
      return {
        data: colleagues,
        columns: columns,
        className: 'colleagues-table colleagues-table-header',
        tableSettings: {
          hasHeader: true,
          paginationHeight: 56,
          headerHeight: 90,
          rowHeight: 70,
          tablePadding: 32,
          pageSize: window.innerWidth > 767 ? 10 : 3,
        },
      };
    },
    [columns]
  );

  if (loading) {
    return (
      <Lottie
        data-testid="loading_colleagues"
        className="lottie"
        loop={true}
        play={true}
        animationData={animationData}
        width={100}
        style={{ width: 100, height: 100, margin: '0px auto' }}
      />
    );
  }

  if (error) {
    logger.error(error);
    toast(error.message, {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  return (
    <div className="container" data-testid="tab_colleagues">
      {employees && employees.length ? (
        <div className="table-wrap">
          <Table
            key="input-filter"
            sortable={true}
            {...computeTableProps(
              mapColleaguesDataToRows(
                searchFuse && searchInputValue ? filterEmployees(searchFuse, searchInputValue) : employees
              )
            )}
          />
        </div>
      ) : (
        <span> {'No colleagues data was provided'}</span>
      )}
    </div>
  );
};

export default withErrorBoundary(TabContactsTable);
